import { useBoltApiClient } from "@bolt/core";
import {
  useGrid,
  SecondaryGrid,
  utils,
  dialogStore,
  PrimaryButton,
} from "@ucl/library";
import React from "react";
import { boltDocumentTemplateGridColumns } from "./boltDocumentTemplateGridColumns";
import { BoltDocumentTemplate, AnnotationDefaults } from "../../types/types";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { BoltDocumentTemplateForm } from "../BoltDocumentTemplateForm/BoltDocumentTemplateFormCreate";

import { BoltDocumentTemplateDesigner } from "../BoltDocumentTemplateDesigner/BoltDocumentTemplateDesigner";

export interface BoltDocumentTemplateGridProps {
  gridKey: string;
  getAuthRequestHeader: () => Promise<Record<string, string>>;
  modelName: string;
  entityType: string;
  onRecordClick?: (record: BoltDocumentTemplate) => void;
  previewBaseODataUrl?: string;
  previewODataKey?: string;
  zoomValue?: number;
  annotationDefaults?: AnnotationDefaults;
}

export const BoltDocumentTemplateGrid: React.FC<
  BoltDocumentTemplateGridProps
> = (props) => {
  const boltApiClient = useBoltApiClient();
  const dialogId = "add-document-template-dialog";
  const designerDialogId = "document-template-designer-dialog";

  const { ready, gridRef, gridSettings } = useGrid<BoltDocumentTemplate>({
    columnsConfiguration: boltDocumentTemplateGridColumns,
    dataSource: `${utils.strings.trimEndForwardSlashForURL(
      boltApiClient.baseApiUrl
    )}/odata/boltdocumenttemplateodata`,
    gridKey: props.gridKey,
    getAuthRequestHeader: props.getAuthRequestHeader,
    allowSearch: true,
    gridConfiguration: {
      allowExcelExport: true,
      onRecordClick: (ev) => {
        if (ev && ev.rowData.id) {
          dialogStore.openDialog({
            id: designerDialogId,
            header: "Document Template Designer",
            allowDragging: false,
            isFullScreen: true,
            content: () => (
              <BoltDocumentTemplateDesigner
                key={ev.rowData.id}
                modelName={props.modelName}
                entityType={props.entityType}
                templateId={ev.rowData.id ?? ""}
                previewBaseODataUrl={
                  props.previewBaseODataUrl ??
                  `${boltApiClient.baseApiUrl}/odata`
                }
                previewODataKey={props.previewODataKey}
                previewGetAuthRequestHeader={props.getAuthRequestHeader}
                zoomValue={props.zoomValue}
                annotationDefaults={props.annotationDefaults}
                onSave={() => {
                  gridRef.current?.refresh();
                  dialogStore.closeDialog(designerDialogId);
                }}
                onDelete={async () => {
                  dialogStore.closeDialog(designerDialogId);
                }}
              />
            ),
            close: () => {
              dialogStore.closeDialog(designerDialogId);
            },
          });

          props.onRecordClick?.(ev.rowData);
        }
      },
    },
  });

  return (
    <section className="bolt-document-template-grid">
      <div className="bolt-document-template-grid-header">
        <PrimaryButton
          text="Add Document Template"
          iconProps={{
            icon: faPlus,
          }}
          onClick={() => {
            dialogStore.openDialog({
              id: dialogId,
              header: "Add Document Template",
              content: () => (
                <BoltDocumentTemplateForm
                  onSave={() => {
                    gridRef.current?.refresh();
                    dialogStore.closeDialog(dialogId);
                  }}
                  onCancel={() => {
                    dialogStore.closeDialog(dialogId);
                  }}
                />
              ),
              close: () => {
                dialogStore.closeDialog(dialogId);
              },
            });
          }}
        />
      </div>
      <SecondaryGrid
        ready={ready}
        gridRef={gridRef}
        gridSettings={gridSettings}
      />
    </section>
  );
};
